import {Manifest} from '../../api/model/manifest';
import {StepNode} from '../../api/model/stepNode';

/** Wie ein Knoten im Vorschlag gegenüber dem gespeicherten Stand abweicht. */
export type RuleProposalMark = 'added' | 'changed';

export interface RuleProposalDiff {
  /** Neue und geänderte Knoten (nodeId → Art) – werden auf der Leinwand markiert. */
  marks: { [nodeId: string]: RuleProposalMark };
  /** Knoten, die der Vorschlag entfernt. Auf der Leinwand nicht mehr sichtbar, deshalb im Banner. */
  removed: StepNode[];
  /** Hat der Vorschlag überhaupt etwas geändert (inkl. erstem Schritt)? */
  hasChanges: boolean;
}

/**
 * Vergleicht ein Regel-Manifest mit einem Vorschlag aus dem chatBIC-Edit-Modus
 * (`Propose-Hybrid-Rule-Change`).
 *
 * Die Position (`inputs.__ui`) zählt nicht als Änderung: der Server setzt sie für neue Knoten, und ein
 * verschobener Knoten ist inhaltlich derselbe.
 */
export function computeRuleProposalDiff(before: Manifest | null | undefined,
                                        after: Manifest | null | undefined): RuleProposalDiff {
  const beforeSteps = new Map((before?.steps ?? []).map(step => [step.nodeId ?? '', step]));
  const afterSteps = new Map((after?.steps ?? []).map(step => [step.nodeId ?? '', step]));
  const marks: { [nodeId: string]: RuleProposalMark } = {};

  for (const [nodeId, step] of afterSteps) {
    const previous = beforeSteps.get(nodeId);
    if (!previous) {
      marks[nodeId] = 'added';
    } else if (stepSignature(previous) !== stepSignature(step)) {
      marks[nodeId] = 'changed';
    }
  }
  const removed = [...beforeSteps.entries()]
    .filter(([nodeId]) => !afterSteps.has(nodeId))
    .map(([, step]) => step);
  const entryChanged = (before?.trigger?.onSuccess ?? null) !== (after?.trigger?.onSuccess ?? null);

  return {marks, removed, hasChanges: Object.keys(marks).length > 0 || removed.length > 0 || entryChanged};
}

function stepSignature(step: StepNode): string {
  const {__ui, ...inputs} = (step.inputs ?? {}) as { [key: string]: any };
  return stableStringify({
    moduleId: step.moduleId,
    inputs,
    settings: step.settings ?? {},
    onSuccess: step.onSuccess ?? null,
    onError: step.onError ?? null,
    onThen: (step as any).onThen ?? null,
    onElse: (step as any).onElse ?? null,
    onBranch: (step as any).onBranch ?? null,
  });
}

/** JSON mit sortierten Schlüsseln – der Server liefert Maps nicht zwingend in Editor-Reihenfolge. */
function stableStringify(value: any): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}
